'use client'

import { tw } from 'utils/classnames'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { SkeletonItem } from 'components/skeleton-item'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { Button } from '@repo/design-system-cf/components/button'

import {
  useClubhousesGetDetailsQuery,
  useClubhouseUpdateDigestPreferenceMutation,
  useClubhousesGetSubscriberCountQuery,
} from '@web/gen/clubhouses'
import { ClubhouseDigestFrequency } from '@web/gen/types'

export function DigestBar({ slug }: { slug: string }) {
  const t = useTranslations('pages.clubhouse.digestbar')
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()
  const [enable, setEnable] = React.useState(false)
  const [digest, setDigest] = React.useState(false)
  const clubhouseUpdateDigestPreferenceMutation = useClubhouseUpdateDigestPreferenceMutation()

  const { data: clubhouse, isPending } = useClubhousesGetDetailsQuery(
    {
      input: {
        clubhouseSlug: slug,
      },
    },
    { select: data => data?.clubhousesGetDetails?.clubhouse },
  )

  const { data: dailyDigestSubscriberCount } = useClubhousesGetSubscriberCountQuery(
    {
      input: {
        clubhouseSlug: slug,
      },
    },
    {
      select: data => data?.clubhousesGetSubscriberCount?.dailyDigestSubscriberCount,
    },
  )

  const handleEnableClick = (e: React.MouseEvent<HTMLElement>) => {
    // oxlint-disable-next-line no-negated-condition
    if (!isLoggedIn) {
      e.preventDefault()
      openModal('register')
    } else {
      clubhouseUpdateDigestPreferenceMutation.mutate(
        {
          input: {
            clubhouseSlug: clubhouse?.slug || slug,
            frequency: ClubhouseDigestFrequency.Daily,
          },
        },
        {
          onSuccess: data => {
            if (data?.clubhousesUpdateDigestPreference.success) {
              setEnable(true)
              setTimeout(() => setDigest(true), 1000)
            }
          },
        },
      )
    }
  }

  if (isPending) {
    return <SkeletonItem className="md:rounded-2 my-3 h-16 md:my-6" />
  }

  if (clubhouse?.isSubscribedToDigest) {
    return null
  }

  return (
    <div
      className={tw(
        'rounded-2 mb-3 flex w-full flex-row items-center justify-center bg-gradient-to-b from-[#647FCC] to-[#324D99] px-4 py-2 opacity-100 transition-all duration-500 ease-in-out sm:h-[52px] sm:py-8 md:mb-6',
        digest && 'pointer-events-none my-0 py-0 opacity-0 md:my-0 lg:h-6',
      )}
    >
      <div className="flex flex-col items-center lg:flex-row xl:whitespace-nowrap">
        <p className="text-cf-white text-[14px] font-normal leading-[20px]">
          {t('daily_digest_text', {
            count: dailyDigestSubscriberCount ?? 0,
            clubhouse: clubhouse?.name ?? '',
          })}
        </p>
      </div>
      <Button
        onClick={handleEnableClick}
        className="bg-cf-white text-cf-sf-gray-1050 ml-3 hover:bg-[#F3EEFE]"
      >
        {enable ? t('signed') : t('enable')}
      </Button>
    </div>
  )
}
